/**
 * api.js — HTTP client for the Express backend
 *
 * All requests include credentials (httpOnly cookie with JWT).
 * Provides typed helpers: api.get(), api.post(), api.put(), api.del()
 *
 * Throws on non-2xx responses with the server's error message.
 */

const BASE = '/api';

class ApiError extends Error {
  /**
   * @param {string} message  server's `error` field
   * @param {number} status   HTTP status
   * @param {object|null} body  full parsed JSON error body — structured refusals
   *   (e.g. the 409 `job_identity_locked`) carry a `code` and the detail the UI
   *   needs to explain itself, which a flattened message string throws away.
   */
  constructor(message, status, body = null) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = body?.code || null;
  }
}

async function request(method, path, body) {
  const opts = {
    method,
    credentials: 'include',
    headers: {},
  };

  // Tell the server which screen this call came from. Every audit row and
  // telemetry row carries it, so "what was the user looking at when this
  // happened" stops being guesswork. See server/lib/changeLog.js.
  try {
    if (typeof window !== 'undefined' && window.location) {
      opts.headers['X-RFOS-Page'] = window.location.pathname.slice(0, 200);
    }
  } catch { /* header is diagnostic only — never block the request */ }

  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, opts);

  if (!res.ok) {
    const text = await res.text();
    let msg;
    let payload = null;
    try {
      payload = JSON.parse(text);
      msg = payload.error || text;
    } catch {
      msg = text || `Request failed (${res.status})`;
    }

    // Role was changed by an admin — force re-login
    if (res.status === 401 && msg === 'role_changed') {
      window.dispatchEvent(new CustomEvent('rfos:role-changed'));
    }

    throw new ApiError(msg, res.status, payload);
  }

  const ct = res.headers.get('content-type');
  if (ct && ct.includes('application/json')) {
    return res.json();
  }
  return null;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  patch: (path, body) => request('PATCH', path, body),
  del: (path) => request('DELETE', path),
};

export { ApiError };
